import { knolToFSRSCard } from "@core/fsrs/scheduler";
import { IonItem, IonLabel, IonList, IonNote } from "@ionic/react";
import MapView from "@lib/mapView";
import { IDeck } from "@models/deck";
import { IKnol } from "@models/knol";
import { ICardResponse } from "@screens/review/types";
import Num from "components/number";
import L10n from "localization";
import { useMemo } from "react";
import Style from "style";
import { ID } from "types/ID";

function GradeChange({ delta }: { delta: number | null }) {
  if (delta === null) {
    return <span>...</span>;
  }

  const gradeChangeStyle = {
    color: (() => {
      if (delta > 0) {
        return Style.colors.easy;
      }
      if (delta < 0) {
        return Style.colors.fail;
      }
    })(),
  };

  const signText = delta > 0 ? "▲ " : delta < 0 ? "▼ " : "";

  // Show 1 digit past the decimal, without a minus sign (that's taken care of by the signText).
  const changeNumber = Math.abs(Math.round(delta * 1000) / 10);
  const changeText = Number.isNaN(changeNumber) ? "" : `${signText}${changeNumber}%`;
  return <span style={gradeChangeStyle}>{changeText}</span>;
}

interface IProps {
  gradeChangeToday: number | null;
  numAverageReviewsPerDay: number | undefined;
  deck?: IDeck;
  knols?: MapView<ID, IKnol>;
  responses?: ICardResponse[];
}
export default function DeckStats(props: IProps) {
  const { gradeChangeToday, numAverageReviewsPerDay, deck, knols, responses } = props;

  // Check if deck is in FSRS mode
  const settings = deck?.user_config?.settings;
  const isFSRS = settings?.reviewMode?.name === "fsrs";

  // Calculate FSRS-specific metrics
  const fsrsMetrics = useMemo(() => {
    if (!isFSRS || !knols || !responses) {
      return null;
    }

    const todayCards = responses.map((r) => r.knol).filter(Boolean);
    if (todayCards.length === 0) return null;

    // Calculate average stability improvement
    let stabilityImprovements = 0;
    let cardsWithStability = 0;
    let cardsMastered = 0; // Cards with stability > 30 days

    for (const knol of todayCards) {
      const fsrsCard = knolToFSRSCard(knol);
      if (fsrsCard && fsrsCard.stability > 0) {
        // For simplicity, we'll assume some improvement happened
        // In a real implementation, you'd track before/after stability
        stabilityImprovements += fsrsCard.stability;
        cardsWithStability++;

        if (fsrsCard.stability > 30) {
          cardsMastered++;
        }
      }
    }

    const avgStability =
      cardsWithStability > 0 ? Math.round(stabilityImprovements / cardsWithStability) : 0;

    // Calculate session success rate (Good/Easy vs Again/Hard for this session only)
    const successfulResponses = responses.filter(
      (r) => r.response === "good" || r.response === "easy",
    ).length;
    const sessionSuccessRate =
      responses.length > 0 ? Math.round((successfulResponses / responses.length) * 100) : 0;

    return {
      avgStability,
      cardsMastered,
      sessionSuccessRate,
    };
  }, [isFSRS, knols, responses]);

  return (
    <IonList>
      <IonItem>
        <IonLabel>{L10n.localize((s) => s.review.numPerDay)}</IonLabel>
        <IonNote slot="end">
          <Num num={numAverageReviewsPerDay} />
        </IonNote>
      </IonItem>
      {isFSRS && fsrsMetrics ? (
        <>
          <IonItem>
            <IonLabel>{L10n.localize((s) => s.review.averageStability)}</IonLabel>
            <IonNote slot="end">
              <span style={{ color: Style.colors.easy }}>{fsrsMetrics.avgStability} days</span>
            </IonNote>
          </IonItem>
          <IonItem>
            <IonLabel>{L10n.localize((s) => s.review.retentionRate)}</IonLabel>
            <IonNote slot="end">
              <span
                style={{
                  color:
                    fsrsMetrics.sessionSuccessRate >= 80
                      ? Style.colors.easy
                      : fsrsMetrics.sessionSuccessRate >= 70
                        ? Style.colors.good
                        : Style.colors.fail,
                }}
              >
                {fsrsMetrics.sessionSuccessRate}%
              </span>
            </IonNote>
          </IonItem>
          {fsrsMetrics.cardsMastered > 0 && (
            <IonItem>
              <IonLabel>{L10n.localize((s) => s.review.cardsMasteredToday)}</IonLabel>
              <IonNote slot="end">
                <span style={{ color: Style.colors.easy }}>
                  <Num num={fsrsMetrics.cardsMastered} />
                </span>
              </IonNote>
            </IonItem>
          )}
        </>
      ) : !isFSRS ? (
        <IonItem>
          <IonLabel>{L10n.localize((s) => s.grade.changeToday)}</IonLabel>
          <IonNote slot="end">
            <GradeChange delta={gradeChangeToday} />
          </IonNote>
        </IonItem>
      ) : null}
    </IonList>
  );
}
